import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Database } from "@/integrations/supabase/types";

type ConsentLog = Database["public"]["Tables"]["consent_logs"]["Row"];

interface UseWithdrawConsentOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export function useWithdrawConsent(options: UseWithdrawConsentOptions = {}) {
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const withdrawConsent = async (consentLogIds: string[]) => {
    if (consentLogIds.length === 0) {
      toast({
        title: "No selections",
        description: "Please select at least one consent log to withdraw.",
        variant: "destructive",
      });
      return;
    }

    setIsWithdrawing(true);

    try {
      // Fetch the consent logs to withdraw
      const { data: logsToWithdraw, error: fetchError } = await supabase
        .from("consent_logs")
        .select("*")
        .in("id", consentLogIds);

      if (fetchError) {
        throw new Error(fetchError.message || "Failed to fetch consent logs");
      }

      if (!logsToWithdraw || logsToWithdraw.length === 0) {
        throw new Error("No consent logs found to withdraw");
      }

      // Get user agent for the withdrawal entry
      const userAgent = navigator.userAgent || null;

      // Create withdrawal entries (new consent logs with granted: false)
      const withdrawalEntries = logsToWithdraw.map((log) => ({
        email: log.email,
        consent_type: log.consent_type,
        policy_version: log.policy_version,
        source_form: "admin_dashboard_withdrawal",
        ip_address: null, // IP address not available on client side
        user_agent: userAgent,
        browser_name: null, // Parsed fields not available on client side
        browser_version: null,
        os_name: null,
        os_version: null,
        device_type: null,
        device_brand: null,
        device_model: null,
        screen_width: null,
        screen_height: null,
        user_id: log.user_id,
        candidate_id: log.candidate_id,
        granted: false,
        timestamp: new Date().toISOString(),
      }));

      const { error: insertError } = await supabase
        .from("consent_logs")
        .insert(withdrawalEntries);

      if (insertError) {
        throw new Error(insertError.message || "Failed to create withdrawal entries");
      }

      // Invalidate queries to refresh the dashboard
      queryClient.invalidateQueries({
        queryKey: ["consent-logs"],
      });

      toast({
        title: "Consent withdrawn",
        description: `Successfully withdrew ${withdrawalEntries.length} consent(s)`,
      });

      options.onSuccess?.();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to withdraw consent";
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });

      options.onError?.(error instanceof Error ? error : new Error(errorMessage));
    } finally {
      setIsWithdrawing(false);
    }
  };

  return {
    withdrawConsent,
    isWithdrawing,
  };
}

